/**
 * Copyright 2024-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { DraggableProvided } from "react-beautiful-dnd";
import { When } from "react-if";
import { ExtendedTooltip, Text, theme } from "@nordcloud/gnui";
import {
  EventActionBatch,
  useEventActionBatchStatusChangedSubscription,
} from "~/generated/graphql";
import { StyledSVGIcon } from "~/components/Styled/StyledSVGIcon";
import { IconWrapper } from "~/components/ToggleItemBox";
import { isNotNil } from "~/tools";
import { getEventActionBatchStatusWithAttention } from "~/views/events/helpers";
import { useGetEventView } from "~/views/events/hooks";
import {
  ActionBatchWrap,
  ActionBatchHeader,
  ActionBatchLabel,
} from "~/views/plans/styled";
import { useEvent } from "../../../EventProvider";

type Props = {
  name?: string | JSX.Element;
  actionBatch: EventActionBatch;
  provided?: DraggableProvided;
  children: React.ReactNode;
  onDelete?: () => void;
  onAddAction?: () => void;
  onClick?: () => void;
  active?: boolean;
};

export function EventActionBatchItem({
  actionBatch,
  children,
  onClick,
  active,
}: Props) {
  const { event: eventCtx } = useEvent();

  const { refetch: refetchEvent } = useGetEventView({
    eventId: eventCtx.id,
  });

  const { data } = useEventActionBatchStatusChangedSubscription({
    variables: {
      input: {
        id: actionBatch.id,
      },
    },
    onData: () => refetchEvent(),
  });

  const statusDetails = getEventActionBatchStatusWithAttention({
    status: data?.eventActionBatchStatusChanged ?? actionBatch.status,
    actions: actionBatch.actions ?? [],
  });

  return (
    <ActionBatchWrap
      id={`batch-${actionBatch.id}`}
      active={active}
      onMouseDown={onClick}
    >
      <ActionBatchLabel>Action Batch</ActionBatchLabel>
      <ActionBatchHeader
        alignItems="center"
        gap={theme.spacing.spacing02}
        paddingLeft={`${theme.spacing.spacing04} !important`}
      >
        <When condition={isNotNil(statusDetails.icon)}>
          <IconWrapper>
            <ExtendedTooltip
              caption={statusDetails?.description}
              status={statusDetails?.tooltipColor}
              position="start"
            >
              <StyledSVGIcon
                name={statusDetails.icon}
                size="md"
                color={statusDetails.color()}
                pathColors={statusDetails.pathColors}
              />
            </ExtendedTooltip>
          </IconWrapper>
        </When>
        <Text
          weight="medium"
          tag="span"
          css={{
            marginRight: "auto",
          }}
        >
          {actionBatch.name}
        </Text>
      </ActionBatchHeader>
      {children}
    </ActionBatchWrap>
  );
}
